/**
 * A plot's dash pattern: the named choice, and the array it draws with.
 *
 * The same bargain `ChartGroup.gridLines` strikes, one level down. `dotted` is
 * the choice a person makes; `[2, 4]` is only how it gets drawn, and an editor
 * that asked for the array would be asking the user to do the renderer's job.
 * `PlotStyle.dash` still wins when present, so an explicit array remains the
 * escape hatch for a pattern the three names cannot say — a lock-step ladder,
 * a dash-dot — without that escape hatch costing everyone else a number pair.
 */

import type { PlotStyle } from "@qufolio/model";

/** The named dash choices. `PlotStyle["dashStyle"]`, minus undefined. */
export type DashStyle = NonNullable<PlotStyle["dashStyle"]>;

export const DEFAULT_DASH_STYLE: DashStyle = "solid";

/**
 * Dash patterns for the named styles, in device pixels: `[on, off, …]`.
 *
 * Deliberately longer than the gridline patterns in `axis.ts`, which are `[4, 4]`
 * and `[1, 3]`. A gridline is one device pixel and a plot is commonly two or
 * three, and a dash pattern is measured along the stroke rather than scaled by
 * its width — so a gridline's dots at plot weight read as a solid line with
 * nicks in it. These are tuned for the 1.5–2px most plots draw at.
 *
 * Solid carries no pattern, because solid *is* the absence of one.
 */
const PLOT_DASH: Record<DashStyle, number[] | undefined> = {
  solid: undefined,
  dashed: [8, 5],
  dotted: [2, 4],
};

/** The dash array a named style draws with, if any. */
export function plotDashFor(style: DashStyle = DEFAULT_DASH_STYLE): number[] | undefined {
  return PLOT_DASH[style];
}

/**
 * The dash array a plot draws with: its explicit `dash`, else its named
 * `dashStyle`, else none. Order is the point — the array is the escape hatch, so
 * it has to beat the name rather than the other way round.
 */
export function resolvePlotDash(style: PlotStyle | undefined): number[] | undefined {
  if (!style) return undefined;
  if (style.dash?.length) return style.dash;
  return plotDashFor(style.dashStyle);
}
